/* eslint-disable camelcase */
import React, { useState, ElementType } from 'react';
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Text,
  useColorModeValue,
  Icon,
  FormErrorMessage,
  VStack,
} from '@chakra-ui/react';
import Select, { SingleValue } from 'react-select';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import {
  useInsertRestaurantOneMutation,
  useSearchSellersWithoutRestaurantLazyQuery,
} from '../utils/__generated__/graphql';
import { useNavigate } from 'react-router-dom';
import { PageHeader, ModernCard } from './shared';

export const AddNewRestaurant = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [insertRestaurant] = useInsertRestaurantOneMutation();
  const [name, setName] = useState('');
  const [nameValid, setNameValid] = useState(true);
  const [value, setValue] = useState<{ id: string; email: string } | undefined>({ id: '', email: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [search, { loading, data }] = useSearchSellersWithoutRestaurantLazyQuery({
    variables: { searchTerm },
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue('brand.500', 'brand.300');

  const options =
    data?.users.map((result) => ({
      value: {
        id: result.id,
        email: result.email,
      },
      label: result.email,
    })) || [];

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameValid(e.target.value.length > 3);
  };

  const handleSelectChange = (newValue: string) => {
    setSearchTerm(newValue);
    search({ variables: { searchTerm: newValue } });
  };

  const handleClick = (opt: SingleValue<{ value: { id: string; email: string }; label: string }>) => {
    setValue(opt?.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (name.length === 0 || value?.email.length === 0) {
      toast({
        title: 'Required fields missing',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const { data } = await insertRestaurant({
        variables: {
          insert_restaurant_inputs: {
            email: value?.email,
            userId: value?.id,
            name: name,
          },
        },
      });

      toast({
        title: 'Success',
        description: 'Restaurant added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate(`/admin/edit-restaurant/${data?.insert_restaurant_one?.id}`);
    } catch (error: any) {
      console.error('Error adding restaurant:', error?.message);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to add restaurant',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <PageHeader
        title="Add New Restaurant"
        subtitle="Create a new restaurant profile"
        actions={
          <Button
            leftIcon={<Icon as={FiArrowLeft as ElementType} />}
            colorScheme="gray"
            variant="outline"
            onClick={() => navigate(-1)}
            size={{ base: 'md', md: 'lg' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Back to Restaurants</Text>
            <Text display={{ base: 'block', sm: 'none' }}>Back</Text>
          </Button>
        }
      />

      <ModernCard p={{ base: 6, md: 8 }}>
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            <FormControl isInvalid={!nameValid}>
              <FormLabel>Restaurant Name</FormLabel>
              <Input
                placeholder="Enter restaurant name"
                value={name}
                onChange={handleNameChange}
                size="lg"
                _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
              />
              <FormErrorMessage>Restaurant name must be longer than 3 characters</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Restaurant Owner</FormLabel>
              <Select
                options={options}
                isLoading={loading}
                isClearable
                isSearchable
                onInputChange={handleSelectChange}
                onChange={(v) => handleClick(v)}
                placeholder="Search for restaurant owner..."
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '40px',
                    borderColor: borderColor,
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: useColorModeValue('gray.300', 'gray.600'),
                    },
                  }),
                }}
              />
            </FormControl>
          </VStack>

          <Flex mt={8} justifyContent="flex-end">
            <Button
              type="submit"
              colorScheme="brand"
              size="lg"
              leftIcon={<Icon as={FiPlus as ElementType} />}
              isDisabled={!nameValid || !value?.email}
              _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Add Restaurant</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Add</Text>
            </Button>
          </Flex>
        </form>
      </ModernCard>
    </>
  );
};
